import apiHelper from '../utils/apiHelper';
import { toast } from 'react-toastify';
import { setItemInIndexedDB, getItemFromIndexedDB } from '../utils/indexedDBHelper';
import extractPdfTextLocally from '../utils/pdfHelper';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const CACHE_KEY = 'repositoryItems';
const CACHE_EXPIRY = 1000 * 60 * 30; // 30 minutes

// Cache management
const cacheManager = {
    async save(items) {
        try {
            await setItemInIndexedDB(CACHE_KEY, {
                items,
                timestamp: Date.now()
            });
        } catch (error) {
            console.warn('Failed to cache repository items:', error);
        }
    },

    async get() {
        try {
            const cache = await getItemFromIndexedDB(CACHE_KEY);
            if (cache && (Date.now() - cache.timestamp) < CACHE_EXPIRY) {
                return cache.items;
            }
        } catch (error) {
            console.warn('Failed to retrieve cached items:', error);
        }
        return null;
    },

    async clear() {
        try {
            await setItemInIndexedDB(CACHE_KEY, null);
        } catch (error) {
            console.warn('Failed to clear repository cache:', error);
        }
    }
};

const handleApiError = (error, customMessage) => {
    const errorMessage = error.response?.data?.details
        || error.response?.data?.error
        || error.message
        || customMessage;

    toast.error(errorMessage);
    throw new Error(errorMessage);
};

const updateDocument = async (documentId, updateData) => {
    try {
        const headers = await apiHelper.getHeaders('graph');

        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/graph/updateDocument/${documentId}`,
            {
                method: 'POST',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updateData)
            }
        );

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to update document');
        }

        return { success: true, data };

    } catch (error) {
        console.error('Error updating document:', error);
        return {
            success: false,
            message: error.message || 'Failed to update document'
        };
    }
};

export const repositoryService = {
    // Main methods
    async getAllItems(onProgress, year = 'all') {

        const yearStr = year?.toString() || 'all';

        try {
            const url = `${API_BASE_URL}/api/graph/getRepositoryItems/${yearStr}`;
            const headers = await apiHelper.getHeaders('getRepositoryItems');
            const response = await fetch(url, { method: 'GET', headers });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();


            let items = [];
            const processItem = (item) => ({
                id: item.id || item.driveId,
                title: item.title || item.name || 'Untitled',
                summary: item.summary || item.description || '',
                type: item.type || item.TypeofDocument || item.contentType?.name || 'Document',
                workingGroup: item.workingGroup || item.WorkingGroupName || '',
                month: item.month || item.Month,
                year: item.year || item.Year,
                mpKey: item.mpKey || item.MPKey,
                qtr: item.qtr || item.QTR,
                publish: item.publish || item.Publish || false,
                publishDate: item.publishDate || item.PublishDate || null,
                doNotShowInSources: item.doNotShowInSources || item.DoNotShowInSources || false,
                // Metadata fields
                modified: item.modified || item.lastModifiedDateTime,
                modifiedBy: item.modifiedBy?.displayName ||
                    item.lastModifiedBy?.user?.displayName ||
                    item.editor?.user?.displayName ||
                    'Unknown',
                created: item.created || item.createdDateTime,
                createdBy: item.createdBy?.displayName ||
                    item.author?.user?.displayName ||
                    'Unknown',
                size: item.size || item.sizeInBytes || 0,
                fileName: item.fileName || item.name,
                fileType: (item.fileName || item.name || '').split('.').pop()?.toLowerCase() || 'unknown',
                url: item.url || item.webUrl,
                // Additional metadata
                contentType: item.contentType?.name || '',
                eTag: item.eTag,
                webUrl: item.webUrl,
                parentReference: item.parentReference,
                shared: item.shared || false,
                sharepointIds: item.sharepointIds,
                size: item.size || null
            });

            if (Array.isArray(data)) {
                items = data.map(processItem);
            } else if (data.items && Array.isArray(data.items)) {
                items = data.items.map(processItem);
            }


            return items;

        } catch (error) {
            console.error('Error in getAllItems:', {
                message: error.message,
                stack: error.stack,
                year: yearStr
            });
            return [];
        }
    },

    async downloadDocument(documentId) {
        try {
            const headers = await apiHelper.getHeaders('getRepositoryFile');
            const response = await fetch(
                `${API_BASE_URL}/api/graph/getRepositoryFile/${documentId}`,
                { headers }
            );

            if (!response.ok) throw new Error('Failed to download document');
            return response.blob();
        } catch (error) {
            toast.error('Failed to download document');
            throw error;
        }
    },

    async uploadDocument(formData) {
        try {


            const headers = await apiHelper.getHeaders('uploadDocument');
            delete headers['Content-Type'];

            const apiFormData = new FormData();
            apiFormData.append('file', formData.get('file'));
            const status = formData.get('status') || '';
            const publishFromStatus = status === 'Published';
            const publishFromField = formData.get('publish') === 'true';
            const isPublished = publishFromStatus || publishFromField;
            
            const metadata = {
                Title: formData.get('title'),
                Summary: formData.get('summary'),
                TypeofDocument: formData.get('type'),
                WorkingGroupName: formData.get('workingGroup'),
                Month: formData.get('month'),
                Year: formData.get('year'),
                MPKey: formData.get('mpKey'),
                QTR: formData.get('qtr'),
                Publish: isPublished,
                PublishDate: isPublished ? formData.get('publishDate') : null,
                DoNotShowInSources: formData.get('doNotShowInSources') === 'true',
                fileUrl: `${process.env.REACT_APP_API_URL}/api/graph/getRepositoryFile/${process.env.REACT_APP_SITE_ID}/${process.env.REACT_APP_REPOSITORY_ID}/${formData.get('file').name}`
            };
            

            
            apiFormData.append('metadata', JSON.stringify(metadata));

            const response = await fetch(`${API_BASE_URL}/api/graph/uploadRepositoryFile`, {
                method: 'POST',
                headers,
                body: apiFormData,
                credentials: 'include'
            });

            if (!response.ok) {
                const error = await response.json();
                console.error('Upload failed:', error);
                throw new Error(error.error || `Upload failed: ${response.statusText}`);
            }

            const result = await response.json();
          

            // Embed into Qdrant
            try {
                const pdfText = await extractPdfTextLocally(formData.get('file'));
               
                await apiHelper.indexQdrantDocument(pdfText, {
                    title: formData.get('title'),
                    summary: formData.get('summary'),
                    type: formData.get('type'),
                    workingGroup: formData.get('workingGroup'),
                    month: formData.get('month'),
                    year: formData.get('year'),
                    mpKey: formData.get('mpKey'),
                    qtr: formData.get('qtr'),
                    publish: formData.get('publish') === 'true',
                    publishDate: formData.get('publish') === 'true' ? formData.get('publishDate') : null,
                    fileUrl: result.file.webUrl,
                    source: formData.get('title'),
                    listItemId: result.metadata?.id
                }, result.metadata?.id);
               
            } catch (indexingError) {
                console.error('⚡ Warning: Failed to index into Qdrant:', indexingError);
            }

            // Clear cache to ensure fresh data on next load
            await cacheManager.clear();

            

            // Return the uploaded document data with the correct ID from the response
            return {
                id: result.file.id || result.file.driveId,  // Try both possible ID fields
                title: result.metadata.Title,
                summary: result.metadata.Summary,
                type: result.metadata.TypeofDocument,
                workingGroup: result.metadata.WorkingGroupName,
                month: result.metadata.Month,
                year: result.metadata.Year,
                mpKey: result.metadata.MPKey,
                qtr: result.metadata.QTR,
                publish: result.metadata.Publish,
                publishDate: result.metadata.PublishDate,
                modified: result.file.lastModifiedDateTime,
                created: result.file.createdDateTime,
                fileName: result.file.name,
                fileType: 'pdf',
                url: result.file.webUrl
            };

        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.message || 'Failed to upload document');
            throw error;
        }
    },

    // Utility methods
    getRepositoryItems: function () {
        return this.getAllItems();
    },

    clearCache: function () {
        return cacheManager.clear();
    },

    // Update document metadata
    updateDocument,

    // Delete a document
    deleteDocument: async (documentId) => {
        try {
            await apiHelper.delete(`graph/deleteRepositoryItem/${process.env.REACT_APP_SITE_ID}/${process.env.REACT_APP_REPOSITORY_ID}/${documentId}`);
            toast.success('Document deleted successfully');
            return true;
        } catch (error) {
            handleApiError(error, 'Failed to delete document');
            return false;
        }
    },

    // Get working groups from SharePoint
    getWorkingGroups: async () => {
        try {
            // First try to get items from cache
            const items = await cacheManager.get();
            const processGroups = (items) => {


                // Get all unique working groups
                const allGroups = [...new Set(items
                    .flatMap(item => {
                        const group = item.workingGroup;
                        // Handle array case
                        if (Array.isArray(group)) {
                            return group.map(g => g.trim()); // Process each group in the array
                        }
                        return typeof group === 'string' ? [group.trim()] : [];
                    })
                    .filter(group =>
                        group &&
                        group.toLowerCase() !== 'n/a' &&
                        group.trim() !== ''
                    )
                )];



                // Group them by type
                const groupedByType = {
                    'Working Group': [],
                    'Specialist Group': [],
                    'Steering Group': [],
                    'Other': []
                };

                allGroups.forEach(group => {
                    if (group.startsWith('Working Group')) {
                        groupedByType['Working Group'].push(group);
                    } else if (group.startsWith('Specialist Group')) {
                        groupedByType['Specialist Group'].push(group);
                    } else if (group.startsWith('Steering Group')) {
                        groupedByType['Steering Group'].push(group);
                    } else {
                        groupedByType['Other'].push(group);
                    }
                });

                // Sort groups within each type
                Object.keys(groupedByType).forEach(type => {
                    groupedByType[type].sort((a, b) => a.localeCompare(b));
                });

                // Return only the groups that have items
                return Object.entries(groupedByType)
                    .filter(([_, groups]) => groups.length > 0)
                    .reduce((acc, [type, groups]) => {
                        acc[type] = groups;
                        return acc;
                    }, {});
            };

            if (items) {
                return processGroups(items);
            }

            // If no cached items, fetch them
            const allItems = await repositoryService.getAllItems();
            return processGroups(allItems);
        } catch (error) {
            console.error('Error getting working groups:', error);
            return {};
        }
    },

    // Simplify the method to not pass IDs that the server already has
    getMPKeyChoices: async () => {
        try {
            const headers = await apiHelper.getHeaders('getColumnChoices');
            const response = await fetch(
                `${API_BASE_URL}/api/graph/getColumnChoices/MPKey`,  // Simplified URL
                { headers }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch MP Key choices');
            }

            const data = await response.json();
            return data.choices || [];
        } catch (error) {
            console.warn('Failed to fetch MP Key choices, using fallback:', error);
            return []; // Return empty array as fallback
        }
    },

    // Add these new methods
    getDocumentTypeChoices: async () => {
        try {
            const headers = await apiHelper.getHeaders('getColumnChoices');
            const response = await fetch(
                `${API_BASE_URL}/api/graph/getColumnChoices/DocumentType`,
                { headers }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch document type choices');
            }

            const data = await response.json();
            return data.choices || [];
        } catch (error) {
            console.warn('Failed to fetch document type choices:', error);
            return [];
        }
    },

    getWorkingGroupChoices: async () => {
        try {
            const headers = await apiHelper.getHeaders('getColumnChoices');
            const response = await fetch(
                `${API_BASE_URL}/api/graph/getColumnChoices/WorkingGroup`,
                { headers }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch working group choices');
            }

            const data = await response.json();
            return data.choices || [];
        } catch (error) {
            console.warn('Failed to fetch working group choices:', error);
            return [];
        }
    }
}; 